import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  AcademicCapIcon,
  BookOpenIcon,
  ClockIcon,
  CheckCircleIcon,
  PlayIcon,
  TrophyIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ArrowRightIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const MyTraining = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('current');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Mock data - in real app this would come from API
  const currentCourses = [
    {
      id: 1,
      title: 'Advanced Healthcare Management',
      instructor: 'Dr. Sarah Wilson',
      progress: 65,
      totalLessons: 42,
      completedLessons: 27,
      nextLesson: 'Healthcare Quality Metrics',
      dueDate: '2025-01-25',
      category: 'Management',
      difficulty: 'Advanced',
      rating: 4.8,
      timeSpent: 8.5,
      estimatedTime: 12,
      lastAccessed: '2025-01-20T14:30:00',
      image: '/images/course-healthcare-mgmt.jpg'
    },
    {
      id: 2,
      title: 'Medical Ethics and Patient Care',
      instructor: 'Prof. Michael Chen',
      progress: 30,
      totalLessons: 28,
      completedLessons: 8,
      nextLesson: 'Informed Consent Procedures',
      dueDate: '2025-02-01',
      category: 'Ethics',
      difficulty: 'Intermediate',
      rating: 4.9,
      timeSpent: 3.2,
      estimatedTime: 10,
      lastAccessed: '2025-01-19T09:15:00',
      image: '/images/course-medical-ethics.jpg'
    },
    {
      id: 3,
      title: 'Emergency Response Protocols',
      instructor: 'Dr. Lisa Rodriguez',
      progress: 85,
      totalLessons: 24,
      completedLessons: 20,
      nextLesson: 'Crisis Communication',
      dueDate: '2025-01-30',
      category: 'Emergency',
      difficulty: 'Advanced',
      rating: 4.7,
      timeSpent: 6.8,
      estimatedTime: 8,
      lastAccessed: '2025-01-21T16:45:00',
      image: '/images/course-emergency.jpg'
    }
  ];

  const completedCourses = [
    {
      id: 4,
      title: 'Patient Communication Skills',
      instructor: 'Dr. Emily Thompson',
      completedDate: '2025-01-10',
      grade: 95,
      certificateId: 'CERT-001',
      category: 'Communication',
      difficulty: 'Beginner',
      rating: 4.9,
      timeSpent: 4.5,
      skills: ['Patient Communication', 'Active Listening', 'Empathy Training'],
      image: '/images/course-communication.jpg'
    },
    {
      id: 5,
      title: 'Digital Health Technologies',
      instructor: 'Dr. James Park',
      completedDate: '2024-12-15',
      grade: 88,
      certificateId: 'CERT-002',
      category: 'Technology',
      difficulty: 'Beginner',
      rating: 4.6,
      timeSpent: 6.2,
      skills: ['Digital Health', 'Telemedicine', 'Health Apps'],
      image: '/images/course-digital-health.jpg'
    },
    {
      id: 6,
      title: 'Healthcare Data Analytics',
      instructor: 'Prof. Anna Kumar',
      completedDate: '2024-11-28',
      grade: 92,
      certificateId: 'CERT-003',
      category: 'Analytics',
      difficulty: 'Intermediate',
      rating: 4.8,
      timeSpent: 8.8,
      skills: ['Data Analysis', 'Predictive Modeling', 'Healthcare Metrics'],
      image: '/images/course-data-analytics.jpg'
    }
  ];

  const learningPaths = [
    {
      id: 1,
      title: 'Healthcare Leadership Excellence',
      description: 'Comprehensive path to develop advanced leadership skills in healthcare',
      totalCourses: 6,
      completedCourses: 3,
      progress: 50,
      estimatedDuration: '24 weeks',
      difficulty: 'Advanced',
      nextCourse: 'Strategic Healthcare Planning'
    },
    {
      id: 2,
      title: 'Clinical Quality Specialist',
      description: 'Master quality improvement and patient safety methodologies',
      totalCourses: 4,
      completedCourses: 1,
      progress: 25,
      estimatedDuration: '16 weeks',
      difficulty: 'Intermediate',
      nextCourse: 'Root Cause Analysis'
    }
  ];

  const upcomingDeadlines = [
    {
      id: 1,
      course: 'Advanced Healthcare Management',
      assignment: 'Module 4 Assessment',
      dueDate: '2025-01-25T14:00:00',
      priority: 'high'
    },
    {
      id: 2,
      course: 'Emergency Response Protocols',
      assignment: 'Final Certification Exam',
      dueDate: '2025-01-30T10:00:00',
      priority: 'high'
    },
    {
      id: 3,
      course: 'Medical Ethics and Patient Care',
      assignment: 'Case Study Analysis',
      dueDate: '2025-02-01T16:00:00',
      priority: 'medium'
    }
  ];

  const achievements = [
    {
      id: 1,
      title: 'Fast Learner',
      description: 'Completed 3 courses in one month',
      icon: TrophyIcon,
      color: 'bg-yellow-500',
      earned: true,
      date: '2025-01-15'
    },
    {
      id: 2,
      title: 'Perfect Score',
      description: 'Achieved 100% on final assessment',
      icon: StarIcon,
      color: 'bg-purple-500',
      earned: true,
      date: '2025-01-10'
    },
    {
      id: 3,
      title: 'Knowledge Seeker',
      description: 'Completed courses from 5 different categories',
      icon: BookOpenIcon,
      color: 'bg-blue-500',
      earned: false,
      progress: 80
    }
  ];

  const filters = [
    { id: 'all', name: 'All Courses' },
    { id: 'management', name: 'Management' },
    { id: 'clinical', name: 'Clinical' },
    { id: 'technology', name: 'Technology' },
    { id: 'ethics', name: 'Ethics' },
    { id: 'emergency', name: 'Emergency' }
  ];

  useEffect(() => {
    // Simulate loading
    setTimeout(() => setIsLoading(false), 800);
  }, []);

  const formatDueDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    if (diffDays > 0) return `Due in ${diffDays} days`;
    return `Overdue by ${Math.abs(diffDays)} days`;
  };

  const formatLastAccessed = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} days ago`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredCurrentCourses = selectedFilter === 'all' 
    ? currentCourses 
    : currentCourses.filter(course => course.category.toLowerCase() === selectedFilter);

  const filteredCompletedCourses = selectedFilter === 'all' 
    ? completedCourses 
    : completedCourses.filter(course => course.category.toLowerCase() === selectedFilter);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-apollo-accent"></div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <AcademicCapIcon className="h-8 w-8 text-apollo-accent mr-3" />
                My Training
              </h1>
              <p className="mt-2 text-gray-600">
                Track your learning progress and achievements
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-apollo-accent focus:border-transparent"
              >
                {filters.map(filter => (
                  <option key={filter.id} value={filter.id}>{filter.name}</option>
                ))}
              </select>
              
              <button
                onClick={() => navigate('/training/catalog')}
                className="bg-apollo-accent text-white px-6 py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center"
              >
                <BookOpenIcon className="h-5 w-5 mr-2" />
                Browse Courses
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-8">
          <nav className="flex space-x-8 px-6">
            {['current', 'completed', 'paths', 'certificates'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                  activeTab === tab
                    ? 'border-apollo-accent text-apollo-accent'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'current' && 'Current Courses'}
                {tab === 'completed' && 'Completed'}
                {tab === 'paths' && 'Learning Paths'}
                {tab === 'certificates' && 'Certificates'}
              </button>
            ))}
          </nav>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {/* Current Courses Tab */}
            {activeTab === 'current' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Current Courses</h2>
                  <span className="text-sm text-gray-600">
                    {filteredCurrentCourses.length} active courses
                  </span>
                </div>

                {filteredCurrentCourses.map((course) => (
                  <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-apollo-accent to-blue-600 rounded-lg flex items-center justify-center">
                          <BookOpenIcon className="h-8 w-8 text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {course.title}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                              {course.difficulty}
                            </span>
                          </div>
                          
                          <p className="text-sm text-gray-600 mb-2">
                            by {course.instructor}
                          </p>
                          
                          <div className="flex items-center text-sm text-gray-600 space-x-4">
                            <span className="flex items-center">
                              <ClockIcon className="h-4 w-4 mr-1" />
                              {course.timeSpent}h / {course.estimatedTime}h
                            </span>
                            <span className="flex items-center">
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              {course.completedLessons}/{course.totalLessons} lessons
                            </span>
                            <span className="text-gray-500">
                              Last accessed {formatLastAccessed(course.lastAccessed)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-apollo-accent mb-1">
                          {course.progress}%
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatDueDate(course.dueDate)}
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-apollo-accent h-3 rounded-full transition-all duration-300"
                          style={{ width: `${course.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Next:</span> {course.nextLesson}
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => navigate(`/training/course/${course.id}`)}
                          className="text-apollo-accent hover:text-apollo-accent/80 text-sm font-medium"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => navigate(`/training/course/${course.id}/learn`)}
                          className="bg-apollo-accent text-white px-4 py-2 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center"
                        >
                          <PlayIcon className="h-4 w-4 mr-1" />
                          Continue
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {filteredCurrentCourses.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                    <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No current courses</h3>
                    <p className="text-gray-600 mb-6">
                      Start learning by enrolling in a course from our catalog.
                    </p>
                    <button
                      onClick={() => navigate('/training/catalog')}
                      className="bg-apollo-accent text-white px-6 py-3 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200"
                    >
                      Browse Courses
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Completed Courses Tab */}
            {activeTab === 'completed' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Completed Courses</h2>
                  <span className="text-sm text-gray-600">
                    {filteredCompletedCourses.length} courses completed
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-6">
                  {filteredCompletedCourses.map((course) => (
                    <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                            <CheckCircleIcon className="h-8 w-8 text-white" />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {course.title}
                              </h3>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(course.difficulty)}`}>
                                {course.difficulty}
                              </span>
                            </div>
                            
                            <p className="text-sm text-gray-600 mb-2">
                              by {course.instructor}
                            </p>
                            
                            <div className="flex items-center text-sm text-gray-600 space-x-4 mb-2">
                              <span className="flex items-center">
                                <CalendarDaysIcon className="h-4 w-4 mr-1" />
                                Completed {new Date(course.completedDate).toLocaleDateString()}
                              </span>
                              <span className="flex items-center">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                {course.timeSpent}h total
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {course.skills.map((skill, index) => (
                                <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600 mb-1">
                            {course.grade}%
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                            {course.rating}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
                        <span className="text-sm text-gray-600">
                          Certificate ID: {course.certificateId}
                        </span>
                        
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => navigate(`/training/certificates/${course.certificateId}`)}
                            className="text-apollo-accent hover:text-apollo-accent/80 text-sm font-medium flex items-center"
                          >
                            <TrophyIcon className="h-4 w-4 mr-1" />
                            View Certificate
                          </button>
                          <button
                            onClick={() => navigate(`/training/course/${course.id}`)}
                            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                          >
                            View Course
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredCompletedCourses.length === 0 && (
                  <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-gray-100">
                    <CheckCircleIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No completed courses yet</h3>
                    <p className="text-gray-600">
                      Complete your first course to see it here.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Learning Paths Tab */}
            {activeTab === 'paths' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">Learning Paths</h2>
                  <span className="text-sm text-gray-600">
                    {learningPaths.length} paths enrolled
                  </span>
                </div>

                {learningPaths.map((path) => (
                  <div key={path.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {path.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(path.difficulty)}`}>
                            {path.difficulty}
                          </span>
                        </div>
                        
                        <p className="text-gray-600 mb-3">
                          {path.description}
                        </p>
                        
                        <div className="flex items-center text-sm text-gray-600 space-x-4">
                          <span className="flex items-center">
                            <BookOpenIcon className="h-4 w-4 mr-1" />
                            {path.completedCourses}/{path.totalCourses} courses
                          </span>
                          <span className="flex items-center">
                            <ClockIcon className="h-4 w-4 mr-1" />
                            {path.estimatedDuration}
                          </span>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-2xl font-bold text-apollo-accent mb-1">
                          {path.progress}%
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-apollo-accent h-3 rounded-full transition-all duration-300"
                          style={{ width: `${path.progress}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="text-sm text-gray-600">
                        <span className="font-medium">Next Course:</span> {path.nextCourse}
                      </div>
                      
                      <button className="text-apollo-accent hover:text-apollo-accent/80 text-sm font-medium flex items-center">
                        View Path Details
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Certificates Tab */}
            {activeTab === 'certificates' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">My Certificates</h2>
                  <span className="text-sm text-gray-600">
                    {completedCourses.length} certificates earned
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {completedCourses.map((course) => (
                    <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                          <TrophyIcon className="h-8 w-8 text-white" />
                        </div>
                        
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {course.title}
                        </h3>
                        
                        <p className="text-sm text-gray-600 mb-2">
                          Certificate of Completion
                        </p>
                        
                        <div className="text-sm text-gray-600">
                          Issued: {new Date(course.completedDate).toLocaleDateString()}
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          ID: {course.certificateId}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="text-sm">
                          <span className="text-gray-600">Grade: </span>
                          <span className="font-medium text-green-600">{course.grade}%</span>
                        </div>
                        
                        <button
                          onClick={() => navigate(`/training/certificates/${course.certificateId}`)}
                          className="bg-apollo-accent text-white px-4 py-2 rounded-lg hover:bg-apollo-accent/90 transition-colors duration-200 flex items-center"
                        >
                          <TrophyIcon className="h-4 w-4 mr-1" />
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Learning Stats */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ChartBarIcon className="h-5 w-5 text-apollo-accent mr-2" />
                Learning Stats
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Courses Completed</span>
                  <span className="font-semibold text-gray-900">{completedCourses.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active Courses</span>
                  <span className="font-semibold text-gray-900">{currentCourses.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Total Hours</span>
                  <span className="font-semibold text-gray-900">
                    {(currentCourses.reduce((acc, course) => acc + course.timeSpent, 0) + 
                      completedCourses.reduce((acc, course) => acc + course.timeSpent, 0)).toFixed(1)}h
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Certificates</span>
                  <span className="font-semibold text-gray-900">{completedCourses.length}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Average Grade</span>
                  <span className="font-semibold text-green-600">
                    {Math.round(completedCourses.reduce((acc, course) => acc + course.grade, 0) / completedCourses.length)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Upcoming Deadlines */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <CalendarDaysIcon className="h-5 w-5 text-apollo-accent mr-2" />
                Upcoming Deadlines
              </h3>
              
              <div className="space-y-4">
                {upcomingDeadlines.map((deadline) => (
                  <div key={deadline.id} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-apollo-accent rounded-full mt-2"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {deadline.assignment}
                      </p>
                      <p className="text-sm text-gray-600">
                        {deadline.course}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">
                          {formatDueDate(deadline.dueDate)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(deadline.priority)}`}>
                          {deadline.priority}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Achievements */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <TrophyIcon className="h-5 w-5 text-apollo-accent mr-2" />
                Achievements
              </h3>
              
              <div className="space-y-4">
                {achievements.map((achievement) => (
                  <div key={achievement.id} className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg ${achievement.color} ${achievement.earned ? '' : 'opacity-50'}`}>
                      <achievement.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${achievement.earned ? 'text-gray-900' : 'text-gray-500'}`}>
                        {achievement.title}
                      </p>
                      <p className={`text-xs ${achievement.earned ? 'text-gray-600' : 'text-gray-400'}`}>
                        {achievement.description}
                      </p>
                      {!achievement.earned && achievement.progress && (
                        <div className="mt-2">
                          <div className="w-full bg-gray-200 rounded-full h-1">
                            <div 
                              className="bg-apollo-accent h-1 rounded-full"
                              style={{ width: `${achievement.progress}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">{achievement.progress}% complete</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default MyTraining;
